import { createSelector } from "@ngrx/store";
import { ignoreNil } from "@obg/rxjs";
import { isArray, isEmpty, isEqual, reduce, split, startsWith } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { RouterState } from "../state";

import { NavigationExtras, QueryParams, Route, RouteState } from "./route.model";

export namespace RouteSelector {
	export const getState = (state: RouterState): RouteState => state.route;

	export const getCurrent = (state: RouterState): Route | undefined => state.route.current;

	export const getCurrent$ = (state$: Observable<RouterState>): Observable<Route> =>
		state$.pipe(
			map((state) => state.route.current),
			ignoreNil(),
			distinctUntilChanged()
		);

	export const getCurrentPathName = (state: RouterState): string | undefined => state.route.current?.pathName;

	export const getName$ = (state$: Observable<RouterState>): Observable<string> =>
		getCurrent$(state$).pipe(map((current) => current.name));

	export const getParams = <T>(state: RouterState): T | undefined => state.route.current?.params as T;

	export const getQueryParams = <T extends QueryParams>(state: RouterState): T | undefined =>
		state.route.current?.queryParams as T;

	export const getData = <T>(state: RouterState): T | undefined => state.route.current?.data as T;

	export const getExtras = (state: RouterState): NavigationExtras | undefined => state.route.current?.extras;

	export const getFragment = (state: RouterState): string | undefined => state.route.current?.fragment;

	export const isActive = (name: string, params?: any) =>
		createSelector(
			getCurrent,
			(current): boolean => current?.name === name && (isEmpty(current.params) || isEqual(params, current.params))
		);

	export const isActiveByUrl = (url: string, match?: string[] | boolean, ignoreQueryParams?: boolean) =>
		createSelector(getCurrent, (current): boolean => {
			if (!current) {
				return false;
			}

			if (isArray(match)) {
				return url === getDeepestCurrentUrlParent(current.externalUrl, match);
			}

			// Ignore the fragment
			let currentExternalUrl = split(current.externalUrl, "#")[0];
			let itemUrl = split(url, "#")[0];

			if (ignoreQueryParams) {
				currentExternalUrl = split(currentExternalUrl, "?")[0];
				itemUrl = split(itemUrl, "?")[0];
			}

			return match
				? currentExternalUrl === itemUrl
				: currentExternalUrl === itemUrl || startsWith(currentExternalUrl, itemUrl + "/");
		});

	export const hasHistory = (state: RouterState): boolean => state.route.hasHistory;

	export const isNavigating = (state: RouterState): boolean => state.route.isNavigating;

	export const shouldActivate = (state: RouterState): boolean | undefined => state.route.shouldActivate;

	/**
	 * Returns the referer of the current route (previous route in case of forward navigation).
	 * Does NOT reflect the history, forward and backward navigation considered the same.
	 *
	 * Use it carefully, should not be abused. Most of the time when referer is needed,
	 * the same functionality can be achieved with `RouteAction.back` or setting `replaceUrl` to `true`.
	 */
	export const getReferer = (state: RouterState): Route | undefined => state.route.referer;

	export const getAdditionalData = (state: RouterState): any => state.route.additionalData;

	export const getSubArea = (state: RouterState): string | undefined => state?.route?.subArea;

	/**
	 * E.g.:  given current url "sportsbook/live/football", "sportsbook/live" will be deeper than "sportsbook/"
	 */
	function getDeepestCurrentUrlParent(currentUrl: string, urlsToMatchFrom: string[]) {
		return reduce(
			urlsToMatchFrom,
			(deepestParent, urlToMatch) => {
				if (startsWith(currentUrl, urlToMatch) && urlToMatch.length > deepestParent.length) {
					return urlToMatch;
				}
				return deepestParent;
			},
			""
		);
	}
}
